import { useEffect, useState } from "react";
import { Pencil, Download, FileText, Loader2, ExternalLink } from "lucide-react";
import { api, API, getToken } from "@/lib/api";
import { formatMoney, formatDate } from "@/lib/format";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
  SheetClose,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const HIDE_IN_LIST = new Set(["category", "currency", "attachments"]);

function AttachmentPreview({ att }) {
  const url = `${API}/attachments/${att.id}/download?auth=${getToken()}`;
  const ct = (att.content_type || "").toLowerCase();
  const name = (att.filename || "").toLowerCase();
  const isPdf = ct.includes("pdf") || name.endsWith(".pdf");
  const isImg = ct.startsWith("image/") || /\.(png|jpe?g|webp|gif)$/.test(name);
  return (
    <div className="rounded-lg border border-border overflow-hidden bg-muted/30" data-testid={`detail-attachment-${att.id}`}>
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border bg-background/40">
        <span className="flex items-center gap-2 text-xs truncate">
          <FileText className="h-3.5 w-3.5 text-brand shrink-0" />
          <span className="truncate" title={att.filename}>{att.filename || "Attachment"}</span>
        </span>
        <a
          href={url}
          download
          target="_blank"
          rel="noreferrer"
          className="shrink-0 text-muted-foreground hover:text-brand p-1"
          title="Download"
          data-testid={`detail-attachment-download-${att.id}`}
        >
          <Download className="h-4 w-4" />
        </a>
      </div>
      {isPdf ? (
        <iframe src={url} title={att.filename} className="w-full h-72 bg-white" />
      ) : isImg ? (
        <img src={url} alt={att.filename} className="w-full max-h-72 object-contain bg-white" />
      ) : (
        <a href={url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 px-3 py-4 text-xs text-brand hover:underline">
          <ExternalLink className="h-3.5 w-3.5" /> Open file
        </a>
      )}
    </div>
  );
}

function ActivitySection({ role, name }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get(`/party-activity?role=${role}&name=${encodeURIComponent(name)}`)
      .then(({ data }) => alive && setData(data))
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [role, name]);

  if (loading)
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground py-3">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading activity…
      </div>
    );
  if (!data) return null;

  const openLabel = role === "debtor" ? "Open Receivables" : "Open Payables";
  const recentLabel = role === "debtor" ? "Recent Income" : "Recent Expenses";

  return (
    <div className="space-y-5" data-testid="detail-activity">
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg border border-danger/25 bg-danger/5 p-3">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Outstanding</p>
          <p className="text-lg font-semibold font-mono text-danger" data-testid="detail-outstanding">
            {formatMoney(data.open_total || 0, data.currency)}
          </p>
          <p className="text-[11px] text-muted-foreground">{data.open_count} open item(s)</p>
        </div>
        <div className="rounded-lg border border-border bg-muted/30 p-3">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Lifetime {role === "debtor" ? "Income" : "Spend"}</p>
          <p className="text-lg font-semibold font-mono" data-testid="detail-lifetime">
            {formatMoney(data.recent_total || 0, data.currency)}
          </p>
          <p className="text-[11px] text-muted-foreground">{data.recent_count} record(s)</p>
        </div>
      </div>

      <div>
        <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mb-2">{openLabel}</p>
        {data.open_items.length === 0 ? (
          <p className="text-xs text-muted-foreground">Nothing outstanding 🎉</p>
        ) : (
          <div className="space-y-1.5">
            {data.open_items.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-2 text-xs rounded-md border border-border px-2.5 py-2" data-testid="detail-open-item">
                <div className="min-w-0">
                  <p className="font-medium truncate">{p.invoice_no || p.category || "—"}</p>
                  <p className="text-muted-foreground">{p.due_date ? `Due ${formatDate(p.due_date)}` : p.date ? formatDate(p.date) : "—"} · {p.status}</p>
                </div>
                <span className="font-mono shrink-0">{formatMoney(p.remaining ?? p.amount, p.currency || data.currency)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mb-2">{recentLabel}</p>
        {data.recent.length === 0 ? (
          <p className="text-xs text-muted-foreground">No activity yet</p>
        ) : (
          <div className="space-y-1.5">
            {data.recent.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-2 text-xs rounded-md border border-border/60 px-2.5 py-2" data-testid="detail-recent-item">
                <div className="min-w-0">
                  <p className="font-medium truncate">{r.category || r.invoice_no || "—"}</p>
                  <p className="text-muted-foreground">{r.date ? formatDate(r.date) : "—"}{r.status ? ` · ${r.status}` : ""}</p>
                </div>
                <span className="font-mono shrink-0">{formatMoney(r.amount, r.currency || data.currency)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Read-only side panel for a record. Shows core fields, an inline preview of any
 * attached invoice/receipt, and (for vendors/debtors) linked ledger activity.
 */
export function EntityDetailDrawer({ config, row, open, onOpenChange, onEdit }) {
  if (!row) return null;

  const titleField = config.detailTitleField || "name";
  const title =
    row[titleField] || row.name || row.vendor || row.client || row.source || "—";

  const attachments = Array.isArray(row.attachments) ? row.attachments : [];

  const fields = config.fields.filter(
    (f) => f.type !== "attachments" && f.name !== titleField && !HIDE_IN_LIST.has(f.name)
  );

  const fmt = (f) => {
    const v = row[f.name];
    if (v === undefined || v === null || String(v).trim() === "") return null;
    if (f.type === "money") return formatMoney(Number(v) || 0, row.currency);
    if (f.type === "date") return formatDate(v);
    return String(v);
  };

  const category = row.category;
  const currency = row.currency;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="entity-detail-drawer">
        <SheetHeader className="text-left">
          <SheetTitle className="font-heading text-2xl break-words" data-testid="detail-name">
            {title}
          </SheetTitle>
          <SheetDescription className="sr-only">Full record details</SheetDescription>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            {category && (
              <Badge className="bg-brand/10 text-brand border-brand/25" data-testid="detail-category">
                {category}
              </Badge>
            )}
            {currency && (
              <Badge variant="outline" className="font-mono" data-testid="detail-currency">
                {currency}
              </Badge>
            )}
          </div>
        </SheetHeader>

        <dl className="mt-6 grid grid-cols-2 gap-x-4 gap-y-4">
          {fields.map((f) => {
            const value = fmt(f);
            const isLong = f.name === "address" || f.name === "description";
            return (
              <div
                key={f.name}
                className={`${isLong ? "col-span-2" : ""} border-b border-border/50 pb-2.5`}
                data-testid={`detail-field-${f.name}`}
              >
                <dt className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1">
                  {f.label}
                </dt>
                <dd
                  className={`text-sm ${value ? "text-foreground" : "text-muted-foreground"} ${
                    isLong ? "whitespace-pre-wrap leading-relaxed" : "font-medium"
                  }`}
                >
                  {value || "—"}
                </dd>
              </div>
            );
          })}
        </dl>

        {attachments.length > 0 && (
          <div className="mt-6 space-y-3">
            <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
              Invoice / Attachments
            </p>
            {attachments.map((a) => (
              <AttachmentPreview key={a.id} att={a} />
            ))}
          </div>
        )}

        {config.activityRole && (
          <div className="mt-7 pt-5 border-t border-border">
            <ActivitySection role={config.activityRole} name={row.name} />
          </div>
        )}

        <SheetFooter className="mt-7 flex-row gap-2 sm:justify-start">
          <Button onClick={onEdit} className="gap-2" data-testid="detail-edit-btn">
            <Pencil className="h-4 w-4" />
            Edit
          </Button>
          <SheetClose asChild>
            <Button variant="outline" data-testid="detail-close-btn">
              Close
            </Button>
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
